import type { InfoSheetContent } from '@uiKit/components/InfoSheet.vue'
import type { CalculateFees } from '@modules/orders'
import { formatAed } from '@shared/utils'
import { DEFAULT_CALCULATOR_AMOUNT } from '../constants/propertyDefaults'
import type { PropertyDetail, PropertyDetailFee } from '../types/propertyDetailTypes'
import { netYieldOf } from './propertyFinancialUtils'

// Pure builders for the right rail's investment calculator: its inputs, the
// pre-quote fee estimate, the return projection and the ⓘ copy per result.

const PERCENT_BASE = 100

// ── Investment calculator ────────────────────────────────────────────────

export interface CalculatorConfig {
  tokenPrice: number
  minInvestment: number
  /** Everything still on sale, in AED. */
  maxInvestment: number
  defaultAmount: number
  netYieldPct: number
  appreciationPct: number
  /** Sum of the investor's percentage fees at purchase — the pre-quote estimate. */
  feeRatePct: number
}

const PRIMARY_SALE_STAGE = 'primary_sell'
const INVESTOR_PAYER = 'investor'

/** Percentage fees the investor pays on a primary purchase ("2%" + "2%" → 4). */
export const estimateFeeRatePct = (fees: PropertyDetailFee[]): number =>
  fees
    .filter((fee) => fee.lifecycleStage === PRIMARY_SALE_STAGE && fee.paidBy === INVESTOR_PAYER)
    .reduce((sum, fee) => {
      const rate = Number(fee.rate?.replace('%', ''))
      return Number.isFinite(rate) ? sum + rate : sum
    }, 0)

export const buildCalculatorConfig = (data: PropertyDetail | null): CalculatorConfig => {
  const tokenPrice = data?.tokenPrice ?? 0
  const minInvestment = Math.max(data?.minInvestmentAmount ?? 0, tokenPrice)
  const maxInvestment = (data?.tokensAvailable ?? 0) * tokenPrice
  const upper = maxInvestment > 0 ? maxInvestment : DEFAULT_CALCULATOR_AMOUNT
  return {
    tokenPrice,
    minInvestment,
    maxInvestment,
    defaultAmount: Math.min(Math.max(DEFAULT_CALCULATOR_AMOUNT, minInvestment), upper),
    netYieldPct: (data && netYieldOf(data)) ?? 0,
    appreciationPct: data?.expectedCapitalAppreciation ?? 0,
    feeRatePct: estimateFeeRatePct(data?.fees ?? []),
  }
}

export interface ReturnProjection {
  rentalIncome: number
  appreciation: number
  expectedValue: number
  /** Appreciation over the horizon as a % of the amount invested. */
  appreciationPct: number
  /** Rent over the horizon as a % of the amount invested. */
  rentalIncomePct: number
  /** Total return over the horizon as a % of the amount invested. */
  roiPct: number
}

/**
 * Rent accrues linearly at the net yield; the property value compounds at the
 * appreciation rate. Expected value = amount + rent + appreciation.
 */
export const projectReturns = (
  amount: number,
  years: number,
  netYieldPct: number,
  appreciationPct: number,
): ReturnProjection => {
  const rentalIncome = (amount * netYieldPct * years) / PERCENT_BASE
  const appreciation = amount * ((1 + appreciationPct / PERCENT_BASE) ** years - 1)
  const gain = rentalIncome + appreciation
  const pctOfAmount = (value: number) => (amount > 0 ? (value / amount) * PERCENT_BASE : 0)
  return {
    rentalIncome,
    appreciation,
    expectedValue: amount + gain,
    appreciationPct: pctOfAmount(appreciation),
    rentalIncomePct: pctOfAmount(rentalIncome),
    roiPct: pctOfAmount(gain),
  }
}

/**
 * ⓘ explainers under the calculator's three result stats — the app's copy
 * verbatim. The app sheets are a single paragraph (no tinted note, no
 * formula), so it renders as the sheet's paragraph alone.
 */
export const PROJECTION_SHEETS: Record<'appreciation' | 'roi' | 'rental', InfoSheetContent> = {
  appreciation: {
    title: 'Appreciation',
    description:
      "The increase in this property's value over your selected time period, based on projected market growth. This is an estimated price gain only.",
  },
  roi: {
    title: 'Estimated ROI',
    description:
      "Your estimated total return on this investment over the selected period, combining both the property's value growth and the rental income you're expected to receive.",
  },
  rental: {
    title: 'Rental Income',
    description:
      "The rental earnings this investment is expected to generate over the selected period, based on the property's net rental yield, paid out to your wallet.",
  },
}

// ── Total cost: fee ⓘ ────────────────────────────────────────────────────

export interface FeeInfo {
  sheet: InfoSheetContent
  heading: string
  formula: string
}

// The app keys fee copy by alias ("acquisitionfee" anywhere in the code or
// name); only the acquisition fee carries an ⓘ there today.
const ACQUISITION_FEE_ALIAS = 'acquisitionfee'

const ACQUISITION_FEE_INFO: FeeInfo = {
  sheet: {
    title: 'Acquisition Fee',
    note: 'A one-time fee charged by Sliceal for facilitating your investment, calculated as a percentage of the amount you invest',
    description:
      "It is applied at the point of purchase and covers the platform's costs of processing, tokenising, and administering your holding.",
  },
  heading: "How it's calculated:",
  formula: 'Acquisition Fee (AED) = Investment Amount × 1%',
}

const normaliseFeeToken = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '')

/** The ⓘ copy for a fee line, or undefined where the app shows none. */
export const feeInfoFor = (code: string, label: string): FeeInfo | undefined =>
  [code, label].some((token) => normaliseFeeToken(token).includes(ACQUISITION_FEE_ALIAS))
    ? ACQUISITION_FEE_INFO
    : undefined

// ── Total cost: fee lines ────────────────────────────────────────────────

export interface CalculatorFeeRow {
  key: string
  /** "DLD Fee (2%)" — the rate is appended when the fee has one. */
  label: string
  /** Formatted amount, e.g. "AED 40.01". */
  value: string
  /** ⓘ explainer, where the app has one for this charge. */
  info?: FeeInfo
}

// Half a fil — anything closer than this is the same amount after rounding.
const ROUNDING_TOLERANCE = 0.005

/**
 * The server quote's fee lines listed under "Total cost", read exactly as the
 * invest flow's fee breakdown reads them: when the lines already add up to
 * the quoted total, `amount` is VAT-inclusive; otherwise the line's VAT is
 * added to it.
 */
export const buildCalculatorFeeRows = (
  quote: Pick<CalculateFees, 'fees' | 'totalFeeAmount'> | null | undefined,
): CalculatorFeeRow[] => {
  if (!quote) return []
  const sum = quote.fees.reduce((total, fee) => total + fee.amount, 0)
  const isVatIncluded = Math.abs(sum - quote.totalFeeAmount) < ROUNDING_TOLERANCE
  return quote.fees.map((fee) => ({
    key: fee.code,
    label: fee.rate ? `${fee.label} (${fee.rate})` : fee.label,
    value: formatAed(isVatIncluded ? fee.amount : fee.amount + fee.vatAmount),
    info: feeInfoFor(fee.code, fee.label),
  }))
}
